// External dependencies
import 'ol/ol.css';
import 'bootstrap/dist/css/bootstrap.min.css';
// if (IS_DEVELOPMENT) {
//   require('bootstrap/dist/css/bootstrap.min.css');
// }

//import 'bootstrap/dist/js/bootstrap.bundle.min';

import '../css/styles.css';
import {
  createMap,
  getSourceFromJSON,
  getSourceLayerFromJSON,
  addLayerPoint,
  addLayerPoints,
  addLayerContour,
  addLayerLine,
  addLayerPolygoneAndPoints,
  addLayerRadialLines,
  fitExtentAndCenterMap,
  _centerMapAndFitExtentByPoints,
  createMarkerPickerToolExternal,
  createGetContourToolExternal,
  addJsonLayer,
  addLayerCluster,
  _displayMapZIndexes,
  _displayMapZoomLevel,
  removeLayersByPrefix,
  removeLayersByName,
  getLayerObjectByName,
  getLayerObjectByKey,
  addOverviewMapControl,
  _getAllClusterSources,
  _getAllLayers,
  addLayerCircle,
} from './maptoolkit.js';
import { getInitLayers } from './layers.js';

import { applyStyleToMap, createSpinner, createMapControls } from './htmlbuilder.js';
import { IsGeoServerAvailable2 } from './utils.js';
import { setMapEvents } from './mapevents.js';

/**
 * Represents the map object. <br/>The map object would detect whether the BR GeoServer is online/offline and would
 * allow the users to select relevant available layers.
 *
 * @constructor
 * @param {string} containerId - The id of the div container for the map
 * @param {Object} [params] - Object containing the following properties
 * @param {Object} [params.chart] - Specify either height and width of the map OR the css default class 'oim-container'
 * @param {string} [params.chart.height] - The height of the map. Not to be provided if params.chart.class is specified
 * @param {string} [params.chart.width] - The width of the map. Not to be provided if params.chart.class is specified
 * @param {string} [params.chart.class='oim-container'] - The css class name for the map style
 * @param {Object} [params.view] - Initial view of the map.
 * @param {Array<number>} [params.view.center=[0, 0]] - Initial center of the map as [long, lat].
 * @param {number} [params.view.zoom=2] - Initial zoom level of the map.
 * @param {Array<string>} [params.layers=['idwmGroup']]  - Specify the layers which can be added to the map from the Add button. <br>
 * The layers that can be added from GeoServer are:
 * <ul>
 * <li>`idwmGroup`: JSON LayerGroup with IDWM country borders, lakes and sea</li>
 * <li>`un11Image`: ImageLayer with high-resolution UN borders</li>
 * <li>`hfbcSitesJson`: JSON VectorLayer with HFBC sites</li>
 * <li>`hfbcCirafZonesJson`: JSON VectorLayer with HFBC Ciraf zones</li>
 * <li>`hfbcCirafQuadrantsJson`: JSON VectorLayer with HFBC quadrants</li>
 * </ul>
 *
 * The layers come from a web server (when GeoServer offline) are:
 * <ul>
 * <li>`idwmGroupWebServer`: JSON LayerGroup with IDWM country borders, lakes and sea</li>
 * </ul>
 *
 * The following basemaps from external sources can also be added:
 * <ul>
 * <li>`osm`: TileLayer OpenStreetMap</li>
 * <li>`terrain`: TileLayer free terrain map from terretris</li>
 * <li>`bingRoads`: TileLayer (non external production)</li>
 * <li>`bingAerial`: TileLayer (non external production)</li>
 * <li>`bingAerialLabels`: TileLayer (non external production)</li>
 * </ul>
 * @param {boolean} [params.isProd=false] - Specify if the library is for external production (no Bing map) or not
 * @param {Array<string>} [params.buttons=['manageLayers']] - Specify the buttons to be showed on the map.
 * The buttons have been designed like in GeoNetwork. Possible values are:
 * <ul>
 * <li>`manageLayers`: To move layers up and down, toggling layers/labels on/off, changing transparency levels, removing layers, etc.</li>
 * <li>`addLayers`: To add layers</li>
 * <li>`importMarkers`: To import data from external file</li>
 * <li>`addMarker`: To add points on the map based on map click or entering coordinates</li>
 * </ul>
 * @param {string} [params.overview] - Layer key (e.g. `idwmGroup`) to display in an overview map control.
 * If `idwmGroup` is requested while GeoServer is offline, `idwmGroupWebServer` is used instead.
 * @param {function(Object)} [params.getCoordinates] - Callback enabling the coordinates picker tool (see {@link olMapBuilder#toggleGetCoordinates}).
 * It receives `{ dd: { long, lat }, dms: { long: { d, m, s, dir }, lat: { d, m, s, dir } } }` for the clicked point.
 * @param {function(Array<Array<number>>)} [params.getContour] - Callback enabling the contour drawing tool (see {@link olMapBuilder#toggleGetContour}).
 * It receives the drawn polygon as an array of [long, lat] coordinates.
 * @returns {Promise<Object|null>} A promise resolving to the map instance, or null if the container is missing.
 */

async function olMapBuilder(containerId, params) {
  if (!containerId) {
    console.error('Please specify a container for the map.');
    return null;
  }
  let mapContainer = document.getElementById(containerId);
  if (!mapContainer) {
    console.error(`Container "${containerId}" not found.`);
    return null;
  }

  let isGeoServerAvailable = await IsGeoServerAvailable2(3000);

  // let display = parentContainer.style.display;
  // parentContainer.style.display = 'none';

  applyStyleToMap(mapContainer, params?.chart);
  let spinner = createSpinner();
  //document.body.appendChild(spinner); //prepare for multiple maps
  mapContainer.appendChild(spinner);
  const map = createMap(mapContainer, params?.view);
  map.updateSize();

  let initLayers = getInitLayers({
    layers: params?.layers,
    isGeoServerAvailable,
    isProd: params?.isProd,
  });

  initLayers.forEach((layer) => {
    if (layer) {
      map.addLayer(layer);
    }
  });

  createMapControls(mapContainer, params, isGeoServerAvailable, map);

  if (params?.overview) {
    addOverviewMapControl(map, params?.overview, isGeoServerAvailable);
  }

  setMapEvents(map, mapContainer); //it is created here because it needs the info div for display of adm/un info

  let drawsExternal = {
    getCoordinates: {
      draw: null,
      source: null,
    },
    getContour: {
      draw: null,
      source: null,
    },
  };

  if (params?.getCoordinates) {
    let drawPicker = createMarkerPickerToolExternal(map, params?.getCoordinates);
    drawsExternal.getCoordinates.draw = drawPicker.draw;
    drawsExternal.getCoordinates.source = drawPicker.source;
  }

  if (params?.getContour) {
    let drawContour = createGetContourToolExternal(map, params?.getContour);
    drawsExternal.getContour.draw = drawContour.draw;
    drawsExternal.getContour.source = drawContour.source;
  }

  // parentContainer.style.display = display;
  spinner.remove();

  /**
   * Style object for the map layer.
   * The style can be entered in three different ways:
   * <ul>
   * <li>`Ol style`: The user can pass an open layer style object</li>
   * <li>`object`: custom object</li>
   * <li>`string`: simple string</li>
   * </ul>
     * @example  
   * simple string
   * style: 'point red 6 14',
     * @example  
     * custom object
   * style: {
      line: {
        stroke: { color: '#cc33ff', width: 3, lineDash: [1, 4] },
      },
    },
   * @typedef {object} LayerStyle
   */

  /**
   * @typedef Styles
   * @type {Object}
   * @property {LayerStyle} [points] - Style for the points. Defaults to {@link styleStar}.
   * @property {LayerStyle} [contour] - Style for the contour. Defaults to {@link stylePolygon}.
   */

  /**
   * @typedef Point
   * @type {Object}
   * @property {number} long - Longitude of the point.
   * @property {number} lat - Latitude of the point.
   * @property {string} [label] - Label of the point.
   */

  /**
   * @typedef ClusterPoint
   * @type {Object}
   * @property {number} long - Longitude of the point.
   * @property {number} lat - Latitude of the point.
   * @property {string} [label] - Label of the point.
   * @property {string} [group] - Group of the point, used to pick the style from `styles.groups`.
   * @property {Object} [props] - Custom properties attached to the feature.
   */

  /**
   * @typedef ClusterStyles
   * @type {Object}
   * @property {string} [all] - Style string applied to all the points.
   * @property {Object<string, string>} [groups] - Style strings by point group, e.g. `{ groupA: 'point red 6' }`.
   * @property {string} [selected] - Style string applied to the selected points.
   */

  /**
   * Events which can be defined to link the features of the layer to external html datatables. Only hover is supported for now.
   * @example
   * // As a common use case, a mouse hover event can be defined to highlight the relevant row of an external table
   * // on the bases of the selected map feature and viceversa.
   * const events = {
   *   hover: function (event, layer, feature, isClick) {
   *     HighlightRowInTable(feature, 'table-P1546P2A', 'P1546P2A', isClick);
   *   },
   * };
   * @typedef LayerEvents
   * @type {Object}
   * @property {function(Object, Object, Object, boolean)} [hover] - Called with (event, layer, feature, isClick) on hover/click of a feature.
   */

  /**
   * Adds a layer with a single point to the current map instance.
   * @param {Object} params - Options for adding the point layer.
   * @param {Point} params.point - The point to display.
   * @param {string} params.layerName - Layer name.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {boolean} [params.hasLabels=false] - If true, labels will be displayed.
   * @param {LayerStyle} [params.style={@link styleStar}] - The style for the layer.
   * @param {string} [params.legend] - Full file name of the legend image.
   * @param {LayerEvents} [params.events] - Events linked to the point of the layer.
   * @param {string} [params.id] - Id assigned to the point feature.
   * @param {boolean} [params.noTooltip=false] - If true, disables the hover tooltip.
   * @returns {Object} The added layer.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addPointLayer = ({ point, layerName, hasDeleteBtn, hasLabels, style, legend, events, id, noTooltip }) => {
    return addLayerPoint({ point, layerName, map, hasDeleteBtn, hasLabels, style, legend, events, id, noTooltip });
  };

  /**
   * Add a layer of points to the current map instance.
   * @param {Object} params - Options for adding the points layer.
   * @param {Point[]} params.points - The array containing the points.
   * @param {string} params.layerName - Layer name.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {boolean} [params.hasLabels=false] - If true, the labels will show up.
   * @param {LayerStyle} [params.style={@link styleStar}] - The style for the layer.
   * @param {string} [params.legend] - Full file name of the legend image.
   * @param {LayerEvents} [params.events] - Events linked to the points of the layer.
   * @param {boolean} [params.noTooltip=false] - If true, disables the hover tooltip.
   * @returns {Object} The added layer.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addPointsLayer = ({ points, layerName, hasDeleteBtn, hasLabels, style, legend, events, noTooltip }) => {
    return addLayerPoints({ points, layerName, map, hasDeleteBtn, hasLabels, style, legend, events, noTooltip });
  };

  /**
   * Get the features of a JSON layer, either from a layer already on the map or directly from GeoServer.
   * Either `layerName` or `layerKey` must be provided; the other one is resolved automatically.
   * @param {Object} params - Options for getting the features.
   * @param {Object} [params.map] - The OpenLayers map to search the layer in. Required when `isFromGeoServer` is false.
   * @param {string} [params.layerName] - Name (label) of the layer.
   * @param {string} [params.layerKey] - Key of the layer (e.g. `hfbcSitesJson`).
   * @param {boolean} [params.isFromGeoServer=false] - If true, the features are loaded from GeoServer instead of the map.
   * @returns {Promise<Array<Object>|undefined>} The features of the layer, or undefined if the layer is not found on the map.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const getFeaturesFromJSON = async ({ map, layerName, layerKey, isFromGeoServer }) => {
    return await getSourceFromJSON({ map, layerName, layerKey, isFromGeoServer });
  };

  /**
   * Get an available layer object (not added to the map) by its key.
   * @param {Object} params - Options for getting the layer.
   * @param {string} params.layerName - Key of the layer (e.g. `hfbcSitesJson`).
   * @returns {Promise<Object|undefined>} The layer object, or undefined if no layer matches the key.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const getLayerFromJSON = async ({ layerName }) => {
    return await getSourceLayerFromJSON({ layerName });
  };

  /**
   * Add a layer with contour points to the current map instance.
   * @param {Object} params - Options for adding the contour layer.
   * @param {Point[]} params.points - The array containing the points of the contour.
   * @param {string} params.layerName - Layer name.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {LayerStyle} [params.style={@link styleShadowDistance}] - The style for the layer.
   * @returns {Object} The added layer.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addContourLayer = ({ points, layerName, hasDeleteBtn, style }) => {
    return addLayerContour({ points, layerName, map, hasDeleteBtn, style });
  };

  /**
   * Add a layer from a geoJSON string to the current map instance.
   * @param {Object} params - Options for adding the JSON layer.
   * @param {string} params.geoJsonString - The GeoJSON string containing the features.
   * @param {string} params.layerName - Layer name.
   * @param {LayerStyle} [params.style] - The style for the layer.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @returns {Object} The added layer.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addLayerJSON = ({ geoJsonString, layerName, style, hasDeleteBtn }) => {
    return addJsonLayer({ map, geoJsonString, layerName, style, hasDeleteBtn });
  };

  /**
   * Add a layer with points and contour to the current map instance.
   * @param {Object} params - Options for adding the polygon and points layer.
   * @param {Point[]} params.points - The array containing the points.
   * @param {string} params.layerName - Layer name.
   * @param {string} [params.unit] - The unit which will appear next to the value for a given point during mouse hover.
   * @param {Styles} [params.styles] - Style for the points and contour.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {boolean} [params.hasLabels=false] - If true, the labels will show up.
   * @param {string} [params.legend] - Full file name of the legend image.
   * @param {LayerEvents} [params.events] - Events linked to the points of the layer.
   * @returns {Object} The added layer.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addPolygonAndPointsLayer = ({ points, layerName, styles, unit, hasDeleteBtn, hasLabels, events, legend }) => {
    return addLayerPolygoneAndPoints({
      points,
      layerName,
      map,
      styles,
      unit,
      hasDeleteBtn,
      hasLabels,
      events,
      legend,
    });
  };

  /**
   * Add a layer with a line connecting the given points to the current map instance.
   * @param {Object} params - Options for adding the line layer.
   * @param {Point[]} params.points - The array containing the points of the line.
   * @param {string} params.layerName - Layer name.
   * @param {LayerStyle} [params.style={@link styleShadowDistance}] - The style for the layer.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {string} [params.legend] - Full file name of the legend image.
   * @returns {Object} The added layer.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addLineLayer = ({ points, layerName, style, hasDeleteBtn, legend }) => {
    return addLayerLine({
      points,
      layerName,
      map,
      style,
      hasDeleteBtn,
      legend,
    });
  };

  /**
   * Add a layer with a circle to the current map instance.
   * @param {Object} params - Options for adding the circle layer.
   * @param {Point} params.center - The center of the circle.
   * @param {number} params.radius - The radius of the circle in km.
   * @param {string} params.layerName - Layer name.
   * @param {LayerStyle} [params.style={@link styleShadowDistance}] - The style for the layer.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {number} [params.noVertices=128] - Number of vertices used to draw the circle.
   * @returns {Object} The added layer.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addCircleLayer = ({ center, radius, layerName, style, hasDeleteBtn, noVertices }) => {
    return addLayerCircle({
      center,
      radius,
      layerName,
      map,
      style,
      hasDeleteBtn,
      noVertices,
    });
  };

  /**
   * Add a layer of clustered points to the current map instance.
   * @param {Object} params - Options for adding the cluster layer.
   * @param {ClusterPoint[]} params.points - The array containing the points.
   * @param {string} params.layerName - Layer name.
   * @param {ClusterStyles} [params.styles] - Styles for the points.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {string} [params.legend] - Full file name of the legend image.
   * @param {Object} [params.events] - Events linked to the points of the layer.
   * @param {function(Object, Object)} [params.events.click] - Called with (event, feature) when an entry of the cluster popup is clicked.
   * @returns {{layerClusters: Object, popup: Object}} The added cluster layer (`layerClusters`) and its popup overlay (`popup`).
   * Pass `layerClusters` to {@link olMapBuilder#centerMapAndFitExtent} to center the map on the points.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addClusterLayer =({ points, layerName, styles, hasDeleteBtn, hasLabels, events, legend }) => {
    return addLayerCluster({
      points,
      layerName,
      map,
      styles,
      hasDeleteBtn,
      hasLabels,
      events,
      legend,
    });
  };

  /**
   * End point of a radial line. Coordinates can also be given as `coord`/`coords` ([long, lat]),
   * `lon`/`lng`/`longitude` and `latitude`, or the end point itself can be a [long, lat] array.
   * Any other property (e.g. `pfd`) can be used as line value through `lineValueKey`.
   * @typedef RadialEndPoint
   * @type {Object}
   * @property {number} long - Longitude of the end point.
   * @property {number} lat - Latitude of the end point.
   * @property {string} [label] - Label of the end point.
   * @property {string} [id] - Id assigned to the end point and line features.
   * @property {string} [info] - Text displayed in the line tooltip (and the point tooltip).
   * @property {(Point|Array<number>)} [start] - Start of the line for this end point. Defaults to `centerPoint`.
   * @property {(Point|Array<number>)} [center] - Alias of `start`.
   */

  /**
   * Styles for the radial lines layer. Each style can also be a style function (feature) => style.
   * @typedef RadialStyles
   * @type {Object}
   * @property {LayerStyle} [center] - Style for the center point (falls back to `point`/`points`). Defaults to {@link styleStar}.
   * @property {LayerStyle} [endPoint] - Style for the end points (falls back to `points`/`point`). Defaults to {@link styleStar}.
   * @property {LayerStyle} [line] - Style for the lines (alias `lines`). Defaults to `'line blue 2'`.
   * @property {LayerStyle} [lineBelow] - Style for the lines whose value is less than or equal to the threshold.
   * Defaults to a green dashed line with the width of `line`.
   * @property {LayerStyle} [lineAbove] - Style for the lines whose value is above the threshold.
   * Defaults to a red line with the width of `line`.
   */

  /**
   * @typedef RadialThreshold
   * @type {Object}
   * @property {number} value - Threshold value.
   * @property {string} [property] - End point property compared to the threshold. Overrides `lineValueKey`.
   * @property {LayerStyle} [belowStyle] - Style for the lines at or below the threshold. Overrides `styles.lineBelow`.
   * @property {LayerStyle} [aboveStyle] - Style for the lines above the threshold. Overrides `styles.lineAbove`.
   */

  /**
   * Add a layer with radial lines connecting a center point to multiple end points.
   * The center point, end points and lines are added as separate sub-layers named
   * `<layerName>(Center)`, `<layerName>(EndPoints)` and `<layerName>(Lines)` within a layer group.
   * @param {Object} params - Options for adding the radial lines layer.
   * @param {(Point|Array<number>)} params.centerPoint - The center point (object or [long, lat]).
   * @param {RadialEndPoint[]} params.endPoints - The array containing the end points.
   * @param {string} params.layerName - Layer name.
   * @param {RadialStyles} [params.styles] - Styles for the center point, end points and lines.
   * @param {boolean} [params.hasDeleteBtn=false] - If true, the delete button will show up.
   * @param {boolean} [params.hasLabels=false] - If true, labels of the points will be displayed.
   * @param {string} [params.legend] - Full file name of the legend image.
   * @param {(number|RadialThreshold)} [params.threshold] - Threshold applied to the line value to pick
   * `styles.lineBelow` (value <= threshold) or `styles.lineAbove`. Lines without a value use `styles.line`.
   * @param {string} [params.unit] - Unit displayed next to the value in the default line tooltip.
   * Defaults to `dB(W/(m²·MHz))` when `lineValueKey` is `pfd`.
   * @param {LayerEvents} [params.events] - Events linked to the center and end points.
   * @param {boolean} [params.noTooltip=false] - If true, disables the points hover tooltip.
   * @param {string} [params.lineValueKey='pfd'] - End point property used as line value, for the threshold and the default line tooltip.
   * @param {(string|function(RadialEndPoint, *): string)} [params.lineInfo] - Line tooltip content:
   * <ul>
   * <li>`string`: name of the end point property to display</li>
   * <li>`function`: called with (endPoint, value), returns the text to display</li>
   * <li>not set: `endPoint.info` if present, otherwise `<lineValueKey>: <value> <unit>`</li>
   * </ul>
   * @param {boolean} [params.showCenter=true] - If true, includes the center point layer.
   * @param {boolean} [params.showEndPoints=true] - If true, includes the end points layer.
   * @param {boolean} [params.showLines=true] - If true, includes the lines layer.
   * @param {boolean} [params.lineTooltip=true] - If true, enables the line hover tooltip (see `params.lineInfo`).
   * @returns {(Object|null)} The added layer group, or null if no sub-layer could be created.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const addRadialLinesLayer = ({
    centerPoint,
    endPoints,
    layerName,
    styles,
    hasDeleteBtn,
    hasLabels,
    legend,
    unit,
    threshold,
    events,
    noTooltip,
    lineValueKey,
    lineInfo,
    showCenter,
    showEndPoints,
    showLines,
    lineTooltip,
  }) => {
    return addLayerRadialLines({
      centerPoint,
      endPoints,
      layerName,
      map,
      styles,
      hasDeleteBtn,
      hasLabels,
      legend,
      threshold,
      unit,
      events,
      noTooltip,
      lineValueKey,
      lineInfo,
      showCenter,
      showEndPoints,
      showLines,
      lineTooltip,
    });
  };

  /**
   * Center the Map. This function should be called after all layers have been added to the map.
   * @param {(Object|Array<Object>)} [layers] - Specify the layers which should be considered for centering the map.
   * Can be either a single layer object or an array of layer objects.
   * Default value is all layers.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const centerMapAndFitExtent = (layers) => {
    fitExtentAndCenterMap(map, layers);
  };

  /**
   * Center the map on the given points. A single point is centered at the given zoom;
   * multiple points are fitted in the view without exceeding the given zoom.
   * @param {Object} params - Options for centering the map.
   * @param {Point[]} params.points - The points to center the map on.
   * @param {number} [params.padding=50] - Padding in pixels around the points.
   * @param {number} [params.zoom=6] - Zoom level for a single point, or maximum zoom level for multiple points.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const centerMapAndFitExtentByPoints = ({ points, padding, zoom }) => {
    _centerMapAndFitExtentByPoints({ map, points, padding, zoom });
  };

  /**
   * Remove from the map all the layers whose name starts with one of the given prefixes.
   * @param {(string|Array<string>)} layersPrefixes - Array of prefixes or comma separated string of prefixes.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const deleteLayersByPrefix = (layersPrefixes) => {
    removeLayersByPrefix(map, layersPrefixes);
  };

  /**
   * Remove from the map the layers with the given names.
   * @param {(string|Array<string>)} layers - Array of layer names or comma separated string of layer names.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const deleteLayersByName = (layers) => {
    removeLayersByName(map, layers);
  };

  /**
   * Get a layer of the map by its key (e.g. `idwmGroup`).
   * @param {string} layerKey - Key of the layer.
   * @returns {(Object|undefined)} The layer, or undefined if not found.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const getLayerByKey = (layerKey) => {
    return getLayerObjectByKey(map, layerKey);
  };

  /**
   * Get a layer of the map by its name.
   * @param {string} layerName - Name of the layer.
   * @returns {(Object|undefined)} The layer, or undefined if not found.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const getLayerByName = (layerName) => {
    return getLayerObjectByName(map, layerName);
  };

  /**
   * Activate or deactivate the coordinates picker tool. Requires `params.getCoordinates` in the constructor.
   * The picked point is cleared on every toggle.
   * @param {boolean} isActive - If true, the tool is activated.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const toggleGetCoordinates = (isActive) => {
    if (drawsExternal.getCoordinates.draw && drawsExternal.getCoordinates.source) {
      drawsExternal.getCoordinates.draw.setActive(isActive);
      drawsExternal.getCoordinates.source.clear();
    }
  };

  /**
   * Activate or deactivate the contour drawing tool. Requires `params.getContour` in the constructor.
   * The drawn contour is cleared on every toggle.
   * @param {boolean} isActive - If true, the tool is activated.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const toggleGetContour = (isActive) => {
    if (drawsExternal.getContour.draw && drawsExternal.getContour.source) {
      drawsExternal.getContour.draw.setActive(isActive);
      drawsExternal.getContour.source.clear();
    }
  };

  /**
   * Debug helper: log to the console the name and zIndex of the map layers (including sub-layers of groups), sorted by zIndex.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const displayMapZIndexes = () => {
    _displayMapZIndexes(map);
  };

  /**
   * Debug helper: log to the console the current zoom level of the map.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const displayMapZoomLevel = () => {
    _displayMapZoomLevel(map);
  };

  /**
   * Get all the cluster sources of the map, including those inside layer groups.
   * @returns {Array<Object>} The cluster sources.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const getAllClusterSources = () => {
    let sources = _getAllClusterSources(map);
    return sources;
  };

  /**
   * Get all the top-level layers of the map.
   * @returns {Array<Object>} The layers.
   * @public
   * @instance
   * @memberof olMapBuilder
   */
  const getAllLayers = () => {
    let allLayers = _getAllLayers(map);
    return allLayers;
  };

  return {
    map,
    addPointLayer,
    addPointsLayer,
    addLineLayer,
    addContourLayer,
    addPolygonAndPointsLayer,
    centerMapAndFitExtent,
    centerMapAndFitExtentByPoints,
    toggleGetCoordinates,
    toggleGetContour,
    addLayerJSON,
    addClusterLayer,
    addRadialLinesLayer,
    deleteLayersByPrefix,
    deleteLayersByName,
    getFeaturesFromJSON,
    getLayerFromJSON,
    getLayerByKey,
    getLayerByName,
    getAllClusterSources,
    getAllLayers,
    isGeoServerAvailable,
    displayMapZIndexes,
    displayMapZoomLevel,
    addCircleLayer,
  };
}

if (typeof module !== 'undefined' && typeof module.exports !== 'undefined') {
  module.exports = olMapBuilder;
} else {
  window.olMapBuilder = olMapBuilder;
}
